import {
  BulbOutlined,
  CheckOutlined,
  LoadingOutlined,
  SearchOutlined,
  ThunderboltOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { Collapse, Space, Typography } from 'antd';
import { TOOL_LABELS } from './toolLabels';
import { SkillTrace, ToolBatch, ToolTraceItem, summarizeArguments } from './trace';

interface Props {
  /** 思考内容（流式累积或历史回放） */
  reasoning?: string;
  /** 按轮次分组的工具调用 */
  toolBatches?: ToolBatch[];
  /** 技能装配过程（流式由 SSE 事件累积，历史回放解析落库的 skillTrace） */
  skillTrace?: SkillTrace;
  /** 回答仍在生成中 */
  streaming?: boolean;
}

/** 尽量把工具入参/结果 JSON 格式化，坏数据原样展示 */
function pretty(raw?: string): string {
  if (!raw) {
    return '';
  }
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function CodeBlock({ text }: { text: string }) {
  return (
    <pre
      style={{
        margin: '4px 0 0',
        padding: 8,
        background: '#fff',
        border: '1px solid #f0f0f0',
        borderRadius: 4,
        fontSize: 12,
        maxHeight: 240,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
      }}
    >
      {text}
    </pre>
  );
}

const panelStyle = {
  marginBottom: 4,
  background: 'rgba(0,0,0,0.02)',
  borderRadius: 6,
};

const labelStyle = { fontSize: 12 };

function toolName(item: ToolTraceItem) {
  return TOOL_LABELS[item.name] || item.name;
}

/**
 * 一批工具的组标题：数量 + 名字 + 状态，不展开也能看出这一步在做什么。
 * 单个工具时顺带入参摘要；一批多个时各说各话，拼在一起反而看不懂，索性不带。
 */
function ToolGroupLabel({ batch }: { batch: ToolBatch }) {
  const runningCount = batch.filter((item) => item.running).length;
  const running = runningCount > 0;
  const names = batch.map(toolName).join('、');
  const detail = batch.length === 1 ? summarizeArguments(batch[0].arguments) : '';

  return (
    <Space size={6} style={labelStyle}>
      <Typography.Text type="secondary" style={labelStyle}>
        {running ? <LoadingOutlined /> : <ToolOutlined />} 调用工具 ({batch.length}) · {names}
      </Typography.Text>
      {detail && (
        <Typography.Text type="secondary" style={{ ...labelStyle, opacity: 0.65 }}>
          · {detail}
        </Typography.Text>
      )}
      <Typography.Text type={running ? 'secondary' : 'success'} style={labelStyle}>
        · {running
          ? `执行中 (${batch.length - runningCount}/${batch.length})`
          : <><CheckOutlined /> 完成</>}
      </Typography.Text>
    </Space>
  );
}

/** 二级列表里单个工具的行标题：名字 + 入参摘要 + 状态 */
function ToolItemLabel({ item }: { item: ToolTraceItem }) {
  const detail = summarizeArguments(item.arguments);
  return (
    <Space size={6} style={labelStyle}>
      <Typography.Text type="secondary" style={labelStyle}>
        {item.running ? <LoadingOutlined /> : <ToolOutlined />} {toolName(item)}
      </Typography.Text>
      {detail && (
        <Typography.Text type="secondary" style={{ ...labelStyle, opacity: 0.65 }}>
          · {detail}
        </Typography.Text>
      )}
      <Typography.Text type={item.running ? 'secondary' : 'success'} style={labelStyle}>
        · {item.running ? '执行中' : <><CheckOutlined /> 完成</>}
      </Typography.Text>
    </Space>
  );
}

/** 单个工具展开后的明细：入参与结果 */
function ToolItemDetail({ item }: { item: ToolTraceItem }) {
  return (
    <>
      {item.arguments && (
        <>
          <Typography.Text type="secondary" style={labelStyle}>
            入参
          </Typography.Text>
          <CodeBlock text={pretty(item.arguments)} />
        </>
      )}
      {item.result && (
        <>
          <Typography.Text type="secondary" style={{ ...labelStyle, display: 'block', marginTop: 8 }}>
            结果
          </Typography.Text>
          <CodeBlock text={pretty(item.result)} />
        </>
      )}
    </>
  );
}

/**
 * 一批工具：组折叠（一级）→ 工具列表（二级）→ 单个工具的入参/结果（点开二级后可见）。
 * 不再在组展开时一次性铺开整批的入参与结果，先给列表、按需下钻。
 */
function ToolBatchGroup({ batch }: { batch: ToolBatch }) {
  return (
    <Collapse
      ghost
      size="small"
      style={panelStyle}
      items={[
        {
          key: 'tools',
          label: <ToolGroupLabel batch={batch} />,
          children: (
            <Collapse
              ghost
              size="small"
              items={batch.map((item, index) => ({
                key: item.toolCallId || String(index),
                label: <ToolItemLabel item={item} />,
                children: <ToolItemDetail item={item} />,
              }))}
            />
          ),
        },
      ]}
    />
  );
}

/** 已加载技能清单：名字 + 适用场景说明 */
function SkillLoadDetail({ skillTrace }: { skillTrace: SkillTrace }) {
  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {(skillTrace.skills || []).map((skill) => (
        <div key={skill.name}>
          <Typography.Text strong style={labelStyle}>
            {skill.name}
          </Typography.Text>
          {skill.description && (
            <Typography.Paragraph
              type="secondary"
              style={{ ...labelStyle, marginBottom: 0, whiteSpace: 'pre-wrap' }}
            >
              {skill.description}
            </Typography.Paragraph>
          )}
        </div>
      ))}
    </Space>
  );
}

/**
 * 技能装配过程：与工具调用同款的两级折叠。
 * 一级组标题给结论（命中 M/N），二级把「搜索技能」「加载技能」列成两步，
 * 各自可再展开看命中名单与技能说明。
 */
function SkillGroup({ skillTrace }: { skillTrace: SkillTrace }) {
  const loaded = skillTrace.skills || [];
  const items = [
    {
      key: 'search',
      label: (
        <Space size={6} style={labelStyle}>
          <Typography.Text type="secondary" style={labelStyle}>
            <SearchOutlined /> 搜索技能
          </Typography.Text>
          <Typography.Text type="secondary" style={{ ...labelStyle, opacity: 0.65 }}>
            · 共 {skillTrace.total} 个，命中 {skillTrace.matched} 个
          </Typography.Text>
          <Typography.Text type="success" style={labelStyle}>
            · <CheckOutlined /> 完成
          </Typography.Text>
        </Space>
      ),
      children: skillTrace.names.length > 0 ? (
        <Typography.Paragraph style={{ ...labelStyle, color: '#595959', marginBottom: 0 }}>
          命中：{skillTrace.names.join('、')}
        </Typography.Paragraph>
      ) : (
        <Typography.Text type="secondary" style={labelStyle}>
          没有命中的技能，按通用方式回答
        </Typography.Text>
      ),
    },
    ...(loaded.length > 0
      ? [
          {
            key: 'load',
            label: (
              <Space size={6} style={labelStyle}>
                <Typography.Text type="secondary" style={labelStyle}>
                  <ThunderboltOutlined /> 加载技能
                </Typography.Text>
                <Typography.Text type="secondary" style={{ ...labelStyle, opacity: 0.65 }}>
                  · {loaded.map((skill) => skill.name).join('、')}
                </Typography.Text>
                <Typography.Text type="success" style={labelStyle}>
                  · <CheckOutlined /> 完成
                </Typography.Text>
              </Space>
            ),
            children: <SkillLoadDetail skillTrace={skillTrace} />,
          },
        ]
      : []),
  ];

  return (
    <Collapse
      ghost
      size="small"
      style={panelStyle}
      items={[
        {
          key: 'skills',
          label: (
            <Space size={6} style={labelStyle}>
              <Typography.Text type="secondary" style={labelStyle}>
                <ThunderboltOutlined /> 技能
              </Typography.Text>
              <Typography.Text type="secondary" style={{ ...labelStyle, opacity: 0.65 }}>
                · 命中 {skillTrace.matched}/{skillTrace.total}
              </Typography.Text>
              <Typography.Text type={skillTrace.matched > 0 ? 'success' : 'secondary'} style={labelStyle}>
                · {skillTrace.matched > 0 ? (
                  <><CheckOutlined /> 已加载 {loaded.length || skillTrace.matched} 个</>
                ) : (
                  '无可用技能'
                )}
              </Typography.Text>
            </Space>
          ),
          children: <Collapse ghost size="small" items={items} />,
        },
      ]}
    />
  );
}

/**
 * 助手气泡上方的过程区：技能装配、深度思考与工具调用**各自独立折叠**（都默认折叠），
 * 正文只保留最终回答。
 *
 * 工具调用按批次（agent loop 的一轮）各成一组，而不是全部堆进一个折叠区——
 * 模型一轮里并发调的几个工具属于同一次决策，是一个整体；下一轮是看过上一轮
 * 结果之后的新决策。一组一步，才看得出「查了什么 → 于是又查了什么」。
 * 组内再按工具下钻：先给列表，点单个工具才看入参/结果全文。
 */
const MessageTrace = ({ reasoning, toolBatches, skillTrace, streaming }: Props) => {
  const batches = (toolBatches || []).filter((batch) => batch.length > 0);
  const hasReasoning = Boolean(reasoning && reasoning.trim());
  if (!hasReasoning && batches.length === 0 && !skillTrace) {
    return null;
  }

  return (
    <>
      {skillTrace && <SkillGroup skillTrace={skillTrace} />}

      {hasReasoning && (
        <Collapse
          ghost
          size="small"
          style={panelStyle}
          items={[
            {
              key: 'reasoning',
              label: (
                <Typography.Text type="secondary" style={labelStyle}>
                  {streaming ? <LoadingOutlined /> : <BulbOutlined />} 深度思考
                </Typography.Text>
              ),
              children: (
                <Typography.Paragraph
                  style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: '#595959', marginBottom: 0 }}
                >
                  {reasoning}
                </Typography.Paragraph>
              ),
            },
          ]}
        />
      )}

      {batches.map((batch, batchIndex) => (
        <ToolBatchGroup key={batchIndex} batch={batch} />
      ))}
    </>
  );
};

export default MessageTrace;
